<script setup lang="ts">
// Web-UI host for the shared @hq/chat-ui CommandPalette (Cmd/Ctrl-K). This file
// owns the COMMAND SET; the shared component owns the chrome + fuzzy filtering +
// keyboard nav. Commands reuse the app's real machinery (router, the launch
// store for agents, the workspace switch, the QuickAsk + theme stores), so a
// palette action is identical to taking it from the nav.
//
// Quick ask is now the palette's first command (the old Cmd-K opened it
// directly; per the UX plan it becomes one command among many). A `dynamic`
// Conversations group runs a debounced server search (GET /v1/api/search?q=)
// so the palette doubles as full-transcript conversation search - hybrid
// (BM25 + dense vector) ranked, message-level, with a matched-text snippet and
// a jump-to-message deep link. See docs/search.md.
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { iconComp } from '@/lib/icon-component';
import type { IconName } from '@/lib/icons';
import { CommandPalette as PaletteUI, type CommandGroup, type CommandItem } from '@hq/chat-ui';
import { api } from '@/api/client';
import { workspacePath } from '@/lib/workspace';
import { usePaletteStore } from '@/stores/palette';
import { useAuthStore } from '@/stores/auth';
import { useQuickAskStore } from '@/stores/quickAsk';
import { useThemeStore } from '@/stores/theme';
import { useLaunchStore } from '@/stores/launch';
import { useToastStore } from '@/stores/toasts';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();
const palette = usePaletteStore();
const auth = useAuthStore();
const quickAsk = useQuickAskStore();
const theme = useThemeStore();
const launch = useLaunchStore();
const toasts = useToastStore();
const router = useRouter();

const isAdmin = computed(() => auth.isAdmin);
const isOperator = computed(() => auth.isOperator);

// Run a command, then let the palette close (the shared component closes on
// select). `go` navigates; `act` wraps a side-effecting action.
function go(to: { name: string; query?: Record<string, string> }): () => void {
  return () => { void router.push(to); };
}

// `label` is an i18n key (resolved with t() in the `groups` computed below).
// The shared nav labels reuse the same nav.* keys as AppNav; the palette-only
// rows (Notifications / Profile / Operator console) get palette.* keys.
interface NavSpec { id: string; label: string; icon: IconName; to: { name: string; query?: Record<string, string> } }
const memberNav: NavSpec[] = [
  { id: 'home', label: 'nav.member.home', icon: 'house', to: { name: 'home' } },
  { id: 'inbox', label: 'nav.member.inbox', icon: 'tray', to: { name: 'inbox' } },
  { id: 'studio', label: 'nav.member.studio', icon: 'layout', to: { name: 'studio-list' } },
  { id: 'files', label: 'nav.member.files', icon: 'folder-open', to: { name: 'files' } },
  { id: 'apps', label: 'nav.member.apps', icon: 'app-window', to: { name: 'apps' } },
  { id: 'schedules', label: 'nav.member.schedules', icon: 'calendar-dots', to: { name: 'schedules' } },
  { id: 'activity', label: 'nav.member.activity', icon: 'pulse', to: { name: 'activity' } },
  { id: 'notifications', label: 'palette.goto.notifications', icon: 'bell', to: { name: 'notifications' } },
  { id: 'me', label: 'nav.account.profile', icon: 'user-circle', to: { name: 'me' } },
  { id: 'help', label: 'nav.help', icon: 'question', to: { name: 'help' } },
];
const adminNav: NavSpec[] = [
  { id: 'connect', label: 'nav.admin.connect', icon: 'plugs-connected', to: { name: 'connect' } },
  { id: 'agents', label: 'nav.admin.agents', icon: 'head-circuit', to: { name: 'specialists' } },
  { id: 'integrations', label: 'nav.admin.integrations', icon: 'plugs', to: { name: 'admin-integrations' } },
  { id: 'skills', label: 'nav.admin.skills', icon: 'books', to: { name: 'skills' } },
  { id: 'email', label: 'nav.admin.email', icon: 'mailbox', to: { name: 'admin-email' } },
  { id: 'channels', label: 'nav.admin.channels', icon: 'hash', to: { name: 'admin-channels' } },
  { id: 'business-profile', label: 'nav.admin.business_profile', icon: 'buildings', to: { name: 'admin-business-profile' } },
  { id: 'admin-schedules', label: 'nav.admin.workspace_schedules', icon: 'calendar-dots', to: { name: 'admin-schedules' } },
  { id: 'audit', label: 'nav.admin.audit', icon: 'scroll', to: { name: 'admin-audit' } },
  { id: 'users', label: 'nav.admin.users', icon: 'users-three', to: { name: 'admin-users' } },
  { id: 'usage', label: 'nav.admin.usage', icon: 'gauge', to: { name: 'admin-usage' } },
  { id: 'settings', label: 'nav.admin.settings', icon: 'gear-six', to: { name: 'admin-settings' } },
];

// ── Workspaces (loaded lazily on open) ───────────────────────────────────────
interface WorkspaceItem { slug: string; name: string; active: boolean; role?: string }
const workspaces = ref<WorkspaceItem[]>([]);
const switching = ref(false);
async function loadWorkspaces(): Promise<void> {
  try {
    const r = await api.get<{ workspaces: WorkspaceItem[] }>('/v1/api/me/workspaces');
    workspaces.value = r.workspaces ?? [];
  } catch { /* leave empty - the switch group just won't show */ }
}
async function switchTo(slug: string): Promise<void> {
  if (switching.value) return;
  switching.value = true;
  try {
    const r = await api.post<{ onboarded?: boolean }>('/v1/api/me/workspaces/switch', { slug });
    window.location.assign(workspacePath(slug, r.onboarded === false ? '/onboarding' : '/'));
  } catch (e) {
    switching.value = false;
    toasts.error((e as { error?: string }).error ?? t('palette.err.switch_workspace'));
  }
}

// ── Open a fresh thread with an agent (create + route, no first message) ──────
async function messageAgent(agentId: string): Promise<void> {
  try {
    const id = await launch.createConversation(agentId);
    void router.push({ name: 'studio', params: { id } });
  } catch (e) {
    toasts.error((e as { error?: string }).error ?? t('palette.err.start_conversation'));
  }
}

// ── Dynamic full-transcript search (GET /v1/api/search?q=) ────────────────────
interface SearchHit {
  conversation_id: string;
  title: string | null;
  agent: { slug: string; display_name: string } | null;
  channel_kind: string;
  seq: number;
  snippet: string;
  score: number;
  matched_at: string;
}
const history = ref<SearchHit[]>([]);
const historyLoading = ref(false);
let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchSeq = 0;
// The snippet carries <mark>…</mark> highlight tags; the palette renders plain
// text, so strip them (the matched text still shows, just unhighlighted).
function plainSnippet(s: string): string {
  return s.replace(/<\/?mark>/g, '').replace(/\s+/g, ' ').trim();
}
function onQuery(q: string): void {
  clearTimeout(searchTimer);
  const term = q.trim();
  if (term.length < 2) { history.value = []; historyLoading.value = false; return; }
  historyLoading.value = true;
  searchTimer = setTimeout(async () => {
    const seq = ++searchSeq;
    try {
      const params = new URLSearchParams({ q: term, limit: '6' });
      const r = await api.get<{ hits: SearchHit[] }>(`/v1/api/search?${params.toString()}`);
      if (seq === searchSeq) history.value = r.hits ?? [];
    } catch {
      if (seq === searchSeq) history.value = [];
    } finally {
      if (seq === searchSeq) historyLoading.value = false;
    }
  }, 250);
}

// Load agents + workspaces the moment the palette opens (cheap, cached).
watch(() => palette.open, (isOpen) => {
  if (!isOpen) { history.value = []; return; }
  void launch.loadAgents();
  void loadWorkspaces();
});

const groups = computed<CommandGroup[]>(() => {
  const out: CommandGroup[] = [];

  // Actions - Quick ask leads (the old Cmd-K target), then thread + theme.
  const actions: CommandItem[] = [
    { id: 'quick-ask', label: t('palette.action.quick_ask'), hint: t('palette.action.quick_ask_hint'), icon: iconComp('sparkle'), perform: () => quickAsk.openPanel() },
    { id: 'new-thread', label: t('palette.action.new_thread'), hint: t('palette.action.new_thread_hint'), icon: iconComp('plus'), perform: go({ name: 'studio-list', query: { new: '1' } }) },
    {
      id: 'toggle-theme',
      label: theme.effective === 'dark' ? t('palette.action.switch_light') : t('palette.action.switch_dark'),
      icon: iconComp(theme.effective === 'dark' ? 'sun' : 'moon'),
      keywords: 'theme dark light appearance',
      perform: () => theme.toggle(),
    },
  ];
  out.push({ id: 'actions', label: t('palette.group.actions'), items: actions });

  // Agents - open a fresh thread with each enabled agent.
  if (launch.enabled.length) {
    out.push({
      id: 'agents',
      label: t('palette.group.agents'),
      items: launch.enabled.map((a) => ({
        id: a.id,
        label: t('palette.message_agent', { name: a.display_name }),
        icon: iconComp('head-circuit'),
        keywords: `${a.display_name} ${a.slug} agent`,
        perform: () => messageAgent(a.id),
      })),
    });
  }

  // Go to - member nav + (admin) admin nav + (operator) the back-office.
  const nav: CommandItem[] = memberNav.map((n) => ({ id: n.id, label: t(n.label), icon: iconComp(n.icon), keywords: 'go to navigate', perform: go(n.to) }));
  if (isAdmin.value) nav.push(...adminNav.map((n) => ({ id: n.id, label: t(n.label), icon: iconComp(n.icon), keywords: 'go to navigate admin', perform: go(n.to) })));
  if (isOperator.value) nav.push({ id: 'operator', label: t('palette.goto.operator_console'), icon: iconComp('shield-check'), keywords: 'platform back office', perform: go({ name: 'operator' }) });
  out.push({ id: 'goto', label: t('palette.group.goto'), items: nav });

  // Switch workspace - only the reachable, non-active ones.
  const others = workspaces.value.filter((w) => !w.active);
  if (others.length) {
    out.push({
      id: 'workspaces',
      label: t('palette.group.switch_workspace'),
      items: others.map((w) => ({
        id: w.slug,
        label: w.name,
        icon: iconComp('buildings'),
        ...(w.role ? { hint: w.role } : {}),
        keywords: `workspace ${w.slug}`,
        perform: () => switchTo(w.slug),
      })),
    });
  }

  // Account.
  out.push({
    id: 'account',
    label: t('palette.group.account'),
    items: [{ id: 'sign-out', label: t('palette.account.sign_out'), icon: iconComp('sign-out'), keywords: 'log out', perform: () => auth.signOut() }],
  });

  // Conversations - DYNAMIC: server-filtered history hits (renders verbatim).
  if (historyLoading.value || history.value.length) {
    out.push({
      id: 'history',
      label: t('palette.group.conversations'),
      dynamic: true,
      items: historyLoading.value
        ? [{ id: '__loading', label: t('palette.searching'), disabled: true, perform: () => {} }]
        : history.value.map((h) => {
            const snip = plainSnippet(h.snippet);
            return {
              // conv:seq so multiple matched messages in one thread are distinct rows.
              id: `${h.conversation_id}:${h.seq}`,
              label: h.title || snip || t('palette.untitled_conversation'),
              icon: iconComp('chat-circle'),
              // Show the matched text; fall back to the agent name when the
              // snippet is already the label (untitled thread).
              hint: h.title ? snip : (h.agent?.display_name ?? ''),
              keywords: h.agent?.display_name ?? '',
              perform: () => {
                void router.push({ name: 'studio', params: { id: h.conversation_id }, query: { seq: String(h.seq) } });
              },
            };
          }),
    });
  }

  return out;
});
</script>

<template>
  <PaletteUI
    :open="palette.open"
    :groups="groups"
    :placeholder="t('palette.placeholder')"
    @update:open="(v) => (palette.open = v)"
    @update:query="onQuery"
  />
</template>
